import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

interface DatePickerFieldProps {
  ariaLabel?: string;
  disabled?: boolean;
  max?: string;
  min?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
}

interface CalendarDay {
  date: Date;
  inCurrentMonth: boolean;
  isoValue: string;
  isDisabled: boolean;
  isToday: boolean;
}

export function DatePickerField({
  ariaLabel,
  disabled = false,
  max,
  min,
  onChange,
  placeholder = "dd/mm/yyyy",
  value
}: DatePickerFieldProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedDate = useMemo(() => parseDateInputValue(value), [value]);
  const minDate = useMemo(() => parseDateInputValue(min), [min]);
  const maxDate = useMemo(() => parseDateInputValue(max), [max]);
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() =>
    getMonthStart(selectedDate ?? minDate ?? new Date())
  );

  useEffect(() => {
    setVisibleMonth(getMonthStart(selectedDate ?? minDate ?? new Date()));
  }, [maxDate, minDate, selectedDate]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const calendarDays = useMemo(
    () => buildCalendarDays(visibleMonth, minDate, maxDate),
    [maxDate, minDate, visibleMonth]
  );
  const canGoPrev = canNavigateMonth(visibleMonth, -1, minDate, maxDate);
  const canGoNext = canNavigateMonth(visibleMonth, 1, minDate, maxDate);
  const todayIso = getDateInputValue(new Date());
  const isTodaySelectable = isDateSelectable(parseDateInputValue(todayIso), minDate, maxDate);
  const isLockedToSingleDate = Boolean(min && max && min === max);

  function handleToggle() {
    if (disabled) {
      return;
    }

    setIsOpen((current) => !current);
  }

  function handleSelect(nextValue: string) {
    onChange(nextValue);
    setIsOpen(false);
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        aria-label={ariaLabel}
        className={cn(
          "input-field input-date-field flex items-center justify-between gap-2.5 px-4 py-2.5 text-left",
          !value && "text-slate/55",
          isOpen && "border-[#1e3a8a]/35 shadow-[0_0_0_4px_rgba(30,58,138,0.1)]"
        )}
        disabled={disabled}
        onClick={handleToggle}
        type="button"
      >
        <span>{selectedDate ? formatDateForDisplay(selectedDate) : placeholder}</span>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] border border-[#1e3a8a]/10 bg-[#f4f7ff] text-[#1e3a8a]">
          <CalendarDays className="h-3.5 w-3.5" />
        </span>
      </button>

      {isOpen ? (
        <div
          aria-label={ariaLabel ? `${ariaLabel} calendar` : "Date picker calendar"}
          className="absolute left-0 top-full z-30 mt-2.5 w-[min(88vw,304px)] overflow-hidden rounded-[22px] border border-line/80 bg-white shadow-[0_18px_44px_rgba(15,23,42,0.14)]"
          role="dialog"
        >
          <div className="border-b border-line/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(248,250,252,0.98)_100%)] px-3.5 py-3.5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate/55">
                  Pick a date
                </p>
                <p className="mt-1 text-base font-semibold text-ink">
                  {visibleMonth.toLocaleDateString("en-IN", {
                    month: "long",
                    year: "numeric"
                  })}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  aria-label="Previous month"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-line/70 bg-white text-slate transition hover:border-[#1e3a8a]/20 hover:bg-[#eef4ff] hover:text-[#1e3a8a] disabled:cursor-not-allowed disabled:opacity-35"
                  disabled={!canGoPrev}
                  onClick={() => setVisibleMonth((current) => shiftMonth(current, -1))}
                  type="button"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  aria-label="Next month"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-line/70 bg-white text-slate transition hover:border-[#1e3a8a]/20 hover:bg-[#eef4ff] hover:text-[#1e3a8a] disabled:cursor-not-allowed disabled:opacity-35"
                  disabled={!canGoNext}
                  onClick={() => setVisibleMonth((current) => shiftMonth(current, 1))}
                  type="button"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-3.5">
            <div className="mb-2.5 grid grid-cols-7 gap-1">
              {WEEKDAY_LABELS.map((label) => (
                <div
                  className="flex h-7 items-center justify-center text-[10px] font-semibold uppercase tracking-[0.16em] text-slate/50"
                  key={label}
                >
                  {label}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day) => {
                const isSelected = day.isoValue === value;

                return (
                  <button
                    aria-label={formatDateForAccessibility(day.date)}
                    className={cn(
                      "flex h-9 items-center justify-center rounded-xl text-[13px] font-semibold transition",
                      day.isDisabled && "cursor-not-allowed text-slate/25",
                      !day.isDisabled && !isSelected && "hover:bg-[#eef4ff] hover:text-[#1e3a8a]",
                      !day.inCurrentMonth && !day.isDisabled && "text-slate/35",
                      day.inCurrentMonth && !day.isDisabled && !isSelected && "text-ink",
                      day.isToday && !isSelected && !day.isDisabled && "border border-[#1e3a8a]/18 bg-[#f8fbff] text-[#1e3a8a]",
                      isSelected &&
                        "bg-[linear-gradient(135deg,#1d4ed8_0%,#2563eb_100%)] text-white shadow-[0_10px_20px_rgba(37,99,235,0.28)]"
                    )}
                    disabled={day.isDisabled}
                    key={day.isoValue}
                    onClick={() => handleSelect(day.isoValue)}
                    type="button"
                  >
                    {day.date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-line/70 bg-[#fbfcfe] px-3.5 py-2.5">
            <button
              className="text-sm font-semibold text-slate transition hover:text-ink disabled:cursor-not-allowed disabled:opacity-35"
              disabled={disabled || isLockedToSingleDate || !value}
              onClick={() => {
                onChange("");
                setIsOpen(false);
              }}
              type="button"
            >
              Clear
            </button>
            <button
              className="text-sm font-semibold text-[#1e3a8a] transition hover:text-[#172554] disabled:cursor-not-allowed disabled:opacity-35"
              disabled={!isTodaySelectable}
              onClick={() => handleSelect(todayIso)}
              type="button"
            >
              Today
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildCalendarDays(visibleMonth: Date, minDate: Date | null, maxDate: Date | null) {
  const monthStart = getMonthStart(visibleMonth);
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - monthStart.getDay());

  return Array.from({ length: 42 }, (_, index): CalendarDay => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);

    return {
      date,
      inCurrentMonth: date.getMonth() === monthStart.getMonth(),
      isoValue: getDateInputValue(date),
      isDisabled: !isDateSelectable(date, minDate, maxDate),
      isToday: getDateInputValue(date) === getDateInputValue(new Date())
    };
  });
}

function canNavigateMonth(
  visibleMonth: Date,
  direction: -1 | 1,
  minDate: Date | null,
  maxDate: Date | null
) {
  const nextMonth = shiftMonth(visibleMonth, direction);
  const monthStart = getMonthStart(nextMonth);
  const monthEnd = getMonthEnd(nextMonth);

  if (minDate && monthEnd < getStartOfDay(minDate)) {
    return false;
  }

  if (maxDate && monthStart > getStartOfDay(maxDate)) {
    return false;
  }

  return true;
}

function isDateSelectable(date: Date | null, minDate: Date | null, maxDate: Date | null) {
  if (!date) {
    return false;
  }

  const normalizedDate = getStartOfDay(date);

  if (minDate && normalizedDate < getStartOfDay(minDate)) {
    return false;
  }

  if (maxDate && normalizedDate > getStartOfDay(maxDate)) {
    return false;
  }

  return true;
}

function parseDateInputValue(value?: string) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const [year, month, day] = value.split("-").map((segment) => Number(segment));
  const nextDate = new Date(year, month - 1, day);

  if (
    Number.isNaN(nextDate.getTime()) ||
    nextDate.getFullYear() !== year ||
    nextDate.getMonth() !== month - 1 ||
    nextDate.getDate() !== day
  ) {
    return null;
  }

  return nextDate;
}

function formatDateForDisplay(date: Date) {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatDateForAccessibility(date: Date) {
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    weekday: "long",
    year: "numeric"
  });
}

function getDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getMonthStart(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getMonthEnd(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getStartOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function shiftMonth(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}
